/*
 * Erscheinungsbild (Branding): Modell, Vererbung und Farbableitung.
 * Vorrang: KaBa-Standard → Installation → Team → Board (B01). Jede Ebene übernimmt die vorherige,
 * nur bewusst gesetzte Werte überschreiben (B02). Aus einer Markenfarbe werden Funktionsfarben für
 * Hell und Dunkel getrennt abgeleitet und auf Kontrast geprüft (V06).
 */

export type BrandScope = 'instance' | 'team' | 'board';

export type LogoChoice = { mode: 'inherit' } | { mode: 'none' } | { mode: 'custom'; main: string; dark?: string | null; mark?: string | null };
export type AccentChoice = { mode: 'inherit' } | { mode: 'custom'; hex: string };
export type HeaderChoice = 'inherit' | 'neutral' | 'tinted';
export type BackgroundDim = 'calm' | 'balanced' | 'strong';
export type BackgroundChoice =
  | { mode: 'inherit' }
  | { mode: 'neutral' }
  | { mode: 'solid'; color: string }
  | { mode: 'gradient'; from: string; to: string }
  | { mode: 'image'; image: string; color: string; focusX: number; focusY: number; dim: BackgroundDim };

export interface BrandingValues {
  logo: LogoChoice;
  accent: AccentChoice;
  header: HeaderChoice;
  background: BackgroundChoice;
}

export const EMPTY_BRANDING: BrandingValues = {
  logo: { mode: 'inherit' },
  accent: { mode: 'inherit' },
  header: 'inherit',
  background: { mode: 'inherit' },
};

/** Herkunft eines wirksamen Werts */
export type BrandOrigin = 'default' | BrandScope;

export const BRAND_ORIGIN_TEXT: Record<BrandOrigin, string> = {
  default: 'KaBa-Standard',
  instance: 'Von der Installation übernommen',
  team: 'Vom Team übernommen',
  board: 'Für dieses Board festgelegt',
};

/** Ausgeliefertes Logo-Set (URLs, im autorisierten Kontext) */
export interface BrandLogo {
  main: string;
  dark: string | null;
  mark: string | null;
  /** Seitenverhältnis Breite/Höhe des Hauptbilds – Platz reservieren (V03) */
  ratio: number;
  markRatio: number | null;
}

export interface ResolvedBranding {
  /** null = kein Logo (ausdrücklich oder nirgends gesetzt) */
  logo: BrandLogo | null;
  logoOrigin: BrandOrigin;
  /** null = KaBa-Standardfarbe */
  accent: string | null;
  accentOrigin: BrandOrigin;
  header: 'neutral' | 'tinted';
  headerOrigin: BrandOrigin;
  background: Exclude<BackgroundChoice, { mode: 'inherit' }> & { url?: string; urls?: Record<string, string> };
  backgroundOrigin: BrandOrigin;
  /** Änderungskennung für Caches */
  version: string;
}

/** Gestaltung einer Einstiegsseite (Gastlink, Einladung, Formular) */
export interface EntryBrand {
  logo: BrandLogo | null;
  accent: string | null;
}

/** Kurzform für Board-Liste und Boardwechsel */
export interface BoardBrand {
  logo: string | null;
  ratio: number;
  mark: string | null;
  accent: string | null;
}

/** Einstellungen einer Ebene für den Dialog: eigene Werte plus das, was ohne sie gelten würde */
export interface BrandingEditState {
  scope: BrandScope;
  values: BrandingValues;
  version: number;
  /** Wirksam mit den eigenen Werten */
  resolved: ResolvedBranding;
  /** Was „Standard übernehmen“ ergeben würde (die übergeordnete Ebene) */
  parent: ResolvedBranding;
  /** Bildquellen für eigene Werte (Asset-ID → URL) */
  assets: Record<string, { url: string; width: number; height: number; kind: 'logo' | 'background' }>;
  canEdit: boolean;
}

export interface BrandingAssetInfo {
  id: string;
  kind: 'logo' | 'background';
  url: string;
  width: number;
  height: number;
}

/** Persönliche Darstellung */
export type ThemeChoice = 'system' | 'light' | 'dark';
export interface Appearance {
  theme: ThemeChoice;
  /** Dekorative Boardbilder ausblenden (Logo und Farbe bleiben) */
  hideDecor: boolean;
}
export const DEFAULT_APPEARANCE: Appearance = { theme: 'system', hideDecor: false };

/* ---------- Vererbung (T05) ---------- */

export interface BrandLevel {
  scope: BrandScope;
  values: BrandingValues;
}

/** Löst eine Kette (Installation → Team → Board) auf; Logo- und Bildauflösung übernimmt der Aufrufer */
export function resolveBrandChain(levels: BrandLevel[]): {
  logo: { set: Extract<LogoChoice, { mode: 'custom' }> | null; origin: BrandOrigin };
  accent: { hex: string | null; origin: BrandOrigin };
  header: { value: 'neutral' | 'tinted'; origin: BrandOrigin };
  background: { value: Exclude<BackgroundChoice, { mode: 'inherit' }>; origin: BrandOrigin };
} {
  let logo: { set: Extract<LogoChoice, { mode: 'custom' }> | null; origin: BrandOrigin } = { set: null, origin: 'default' };
  let accent: { hex: string | null; origin: BrandOrigin } = { hex: null, origin: 'default' };
  let header: { value: 'neutral' | 'tinted'; origin: BrandOrigin } = { value: 'neutral', origin: 'default' };
  let background: { value: Exclude<BackgroundChoice, { mode: 'inherit' }>; origin: BrandOrigin } = { value: { mode: 'neutral' }, origin: 'default' };
  for (const l of levels) {
    const v = l.values;
    // „Kein Logo“ wird nicht durch ein übergeordnetes ersetzt; ein Set wird nie gemischt (T02)
    if (v.logo.mode === 'none') logo = { set: null, origin: l.scope };
    else if (v.logo.mode === 'custom') logo = { set: v.logo, origin: l.scope };
    if (v.accent.mode === 'custom') accent = { hex: normalizeHex(v.accent.hex), origin: l.scope };
    if (v.header !== 'inherit') header = { value: v.header, origin: l.scope };
    if (v.background.mode !== 'inherit') background = { value: v.background, origin: l.scope };
  }
  return { logo, accent, header, background };
}

/** Alle Asset-IDs einer Werteebene */
export function brandingAssetIds(v: BrandingValues): string[] {
  const ids: string[] = [];
  if (v.logo.mode === 'custom') ids.push(v.logo.main, ...(v.logo.dark ? [v.logo.dark] : []), ...(v.logo.mark ? [v.logo.mark] : []));
  if (v.background.mode === 'image') ids.push(v.background.image);
  return ids;
}

/* ---------- Farben ---------- */

export const BRAND_PALETTE: { name: string; hex: string }[] = [
  { name: 'KaBa-Blau', hex: '#3b5bdb' },
  { name: 'Petrol', hex: '#0b7285' },
  { name: 'Grün', hex: '#2b8a3e' },
  { name: 'Violett', hex: '#7048e8' },
  { name: 'Magenta', hex: '#c2255c' },
  { name: 'Rot', hex: '#e03131' },
  { name: 'Orange', hex: '#e8590c' },
  { name: 'Gelb', hex: '#fcc419' },
  { name: 'Graphit', hex: '#343a40' },
];

export const DEFAULT_ACCENT = '#3b5bdb';

export function normalizeHex(v: string): string | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(v.trim());
  if (!m) return null;
  let h = m[1]!.toLowerCase();
  if (h.length === 3) h = h.replace(/./g, (c) => c + c);
  return `#${h}`;
}

type RGB = [number, number, number];
const hexToRgb = (hex: string): RGB => {
  const h = normalizeHex(hex) ?? DEFAULT_ACCENT;
  return [parseInt(h.slice(1, 3), 16) / 255, parseInt(h.slice(3, 5), 16) / 255, parseInt(h.slice(5, 7), 16) / 255];
};
const rgbToHex = (c: RGB) => `#${c.map((x) => Math.round(Math.min(1, Math.max(0, x)) * 255).toString(16).padStart(2, '0')).join('')}`;
const toLin = (x: number) => (x <= 0.04045 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4);
const fromLin = (x: number) => (x <= 0.0031308 ? 12.92 * x : 1.055 * x ** (1 / 2.4) - 0.055);

/** Relative Leuchtdichte nach WCAG */
export function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map(toLin) as RGB;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

type LCH = [number, number, number];
function hexToOklch(hex: string): LCH {
  const [r, g, b] = hexToRgb(hex).map(toLin) as RGB;
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  return [L, Math.hypot(A, B), Math.atan2(B, A)];
}
function oklchToRgb([L, C, h]: LCH): RGB {
  const A = C * Math.cos(h);
  const B = C * Math.sin(h);
  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s = (L - 0.0894841775 * A - 1.291485548 * B) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}
/** OKLCH → HEX, Sättigung bis in den darstellbaren Bereich verringern */
function oklchToHex(c: LCH): string {
  let [L, C, h] = c;
  L = Math.min(1, Math.max(0, L));
  for (let i = 0; i < 40; i++) {
    const lin = oklchToRgb([L, C, h]);
    if (lin.every((x) => x >= -0.0005 && x <= 1.0005)) return rgbToHex(lin.map(fromLin) as RGB);
    C *= 0.92;
  }
  return rgbToHex(oklchToRgb([L, 0, h]).map(fromLin) as RGB);
}

/** Helligkeit schrittweise ändern, bis die Bedingung erfüllt ist */
function adjust(hex: string, dir: -1 | 1, ok: (h: string) => boolean): string {
  if (ok(hex)) return hex;
  const [L0, C, h] = hexToOklch(hex);
  for (let L = L0; L >= 0 && L <= 1; L += dir * 0.01) {
    const cand = oklchToHex([L, C, h]);
    if (ok(cand)) return cand;
  }
  return dir < 0 ? '#000000' : '#ffffff';
}

/** Mischung im sRGB-Farbraum (wie CSS color-mix in srgb) */
export function mix(a: string, b: string, t: number): string {
  const x = hexToRgb(a);
  const y = hexToRgb(b);
  return rgbToHex(x.map((v, i) => v * t + y[i]! * (1 - t)) as RGB);
}

export const THEME_SURFACES = {
  light: { surface: '#f6f7f9', panel: '#ffffff' },
  dark: { surface: '#14161a', panel: '#1f2228' },
} as const;

export const THEME_INK = {
  light: { ink: '#1d2128', inkSoft: '#5d6574' },
  dark: { ink: '#e8eaee', inkSoft: '#9aa2b1' },
} as const;

export interface AccentTokens {
  /** Füllfarbe für Buttons, Auswahl, Rahmen – ≥ 3:1 zu Fläche und Panel */
  accent: string;
  /** Schrift auf der Füllfarbe – ≥ 4,5:1 */
  onAccent: string;
  /** Akzentschrift und Links – ≥ 4,5:1 zu Fläche, Panel und weicher Auswahl */
  text: string;
  /** Weiche Auswahl */
  soft: string;
  /** Getönter Kopf */
  tint: string;
}

const DARK_INK = '#111827';

/** Funktionsfarben aus einer Markenfarbe ableiten, getrennt für Hell und Dunkel (V06) */
export function deriveAccent(brand: string, mode: 'light' | 'dark'): AccentTokens {
  const { surface, panel } = THEME_SURFACES[mode];
  const dir: -1 | 1 = mode === 'light' ? -1 : 1;
  const base = normalizeHex(brand) ?? DEFAULT_ACCENT;
  // 1. Fläche sichtbar gegen den Untergrund
  let accent = adjust(base, dir, (h) => contrast(h, panel) >= 3 && contrast(h, surface) >= 3);
  // 2. Schrift darauf: Weiß oder dunkel; sonst Fläche weiter verschieben
  let onAccent: string;
  if (contrast(accent, '#ffffff') >= 4.5) onAccent = '#ffffff';
  else if (contrast(accent, DARK_INK) >= 4.5) onAccent = DARK_INK;
  else if (mode === 'light') {
    accent = adjust(accent, -1, (h) => contrast(h, '#ffffff') >= 4.5);
    onAccent = '#ffffff';
  } else {
    accent = adjust(accent, 1, (h) => contrast(h, DARK_INK) >= 4.5);
    onAccent = DARK_INK;
  }
  const { ink, inkSoft } = THEME_INK[mode];
  // 3. Weiche Auswahl und Schrift in Akzentfarbe; bei kräftigen Marken die Auswahl zurücknehmen,
  //    damit normale Schrift darauf lesbar bleibt
  let soft = panel as string;
  let text = base;
  for (let t = mode === 'light' ? 0.12 : 0.24; t > -0.001; t -= 0.02) {
    soft = mix(base, panel, t);
    const s = soft;
    text = adjust(base, dir, (h) => contrast(h, panel) >= 4.5 && contrast(h, surface) >= 4.5 && contrast(h, s) >= 4.5);
    if (contrast(text, soft) >= 4.5 && contrast(ink, soft) >= 4.5) break;
  }
  // 4. Getönter Kopf: auch gedämpfte Schrift muss darauf lesbar bleiben
  let tint = surface as string;
  for (let t = mode === 'light' ? 0.08 : 0.14; t > -0.001; t -= 0.02) {
    tint = mix(base, surface, t);
    // Auch mit den leichten Schaltflächenflächen im Kopf (schwarz 5 % bzw. weiß 10 %)
    const overlay = mode === 'light' ? mix('#000000', tint, 0.05) : mix('#ffffff', tint, 0.1);
    if (contrast(inkSoft, tint) >= 4.5 && contrast(inkSoft, overlay) >= 4.5 && contrast(ink, tint) >= 4.5) break;
  }
  return { accent, onAccent, text, soft, tint };
}

/** KaBa-Standard ohne Markenfarbe: bisherige Werte; dunkle Akzentschrift lesbar gemacht */
export const DEFAULT_TOKENS: Record<'light' | 'dark', AccentTokens> = {
  light: { accent: '#3b5bdb', onAccent: '#ffffff', text: '#3b5bdb', soft: '#e8edff', tint: '#eef1fb' },
  dark: { accent: '#3b5bdb', onAccent: '#ffffff', text: '#8da2fb', soft: '#232b48', tint: '#191d2b' },
};

export function accentTokens(hex: string | null, mode: 'light' | 'dark'): AccentTokens {
  return hex ? deriveAccent(hex, mode) : DEFAULT_TOKENS[mode];
}

/** Kurzer Kontrastbericht für die Vorschau */
export function accentReport(hex: string): { mode: 'light' | 'dark'; adjusted: boolean; darkText: boolean }[] {
  return (['light', 'dark'] as const).map((mode) => {
    const t = deriveAccent(hex, mode);
    return { mode, adjusted: t.accent !== normalizeHex(hex) || t.text !== normalizeHex(hex), darkText: t.onAccent !== '#ffffff' };
  });
}
